import { ApolloLink, Observable } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';

const inFlight = new Map();

const isQuery = (operation) => {
	const definition = getMainDefinition(operation.query);
	return definition.kind === 'OperationDefinition' && definition.operation === 'query';
};

/**
 * Opt-in link: when an operation's context carries `cancelGroup`, any earlier
 * in-flight operation sharing that key is aborted. Callers without a
 * `cancelGroup` pass through untouched — the link is a no-op by default.
 *
 * Only queries can be cancelled. Mutations and subscriptions are never
 * aborted, even if they set a cancelGroup.
 *
 * The abort surfaces as `complete` to the aborted observer (not `error`) so
 * store code awaiting `client.query` gets a resolved-but-empty result rather
 * than a thrown `AbortError`. Real network/graphql errors still flow through.
 */
export const cancelPreviousLink = new ApolloLink((operation, forward) => {
	const context = operation.getContext();
	const key = context.cancelGroup;

	if (!key || !isQuery(operation)) {
		return forward(operation);
	}

	return new Observable((observer) => {
		const previous = inFlight.get(key);
		if (previous) previous.abort();

		const controller = new AbortController();
		inFlight.set(key, controller);

		operation.setContext(({ fetchOptions = {} }) => ({
			fetchOptions: { ...fetchOptions, signal: controller.signal },
		}));

		const clearIfCurrent = () => {
			if (inFlight.get(key) === controller) inFlight.delete(key);
		};

		const subscription = forward(operation).subscribe({
			next: (value) => observer.next(value),
			error: (err) => {
				clearIfCurrent();
				if (err?.name === 'AbortError' || controller.signal.aborted) {
					observer.complete();
					return;
				}
				observer.error(err);
			},
			complete: () => {
				clearIfCurrent();
				observer.complete();
			},
		});

		return () => subscription.unsubscribe();
	});
});
